import { useState } from "react";
import JalaliDatePicker from "@/components/JalaliDatePicker";
import { formatJalaliDate } from "@/lib/format";
import { formatNumber } from "@/features/reports/lib/format";

import { useGeoDistribution } from "../hooks/useGeoDistribution";
import IranProvinceMap from "../components/IranProvinceMap";
import GeoBarChart from "../components/GeoBarChart";
import GeoHeatmap from "../components/GeoHeatmap";
import GeoReports from "../components/GeoReports";
import { fa } from "../i18n/fa";
import "../traceability.css";

export default function GeoDistributionPage() {
  const [product, setProduct] = useState("beef");
  const [granularity, setGranularity] = useState("week");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [hideEmptyProvinces, setHideEmptyProvinces] = useState(false);
  const { data, loading, error } = useGeoDistribution({
    product,
    granularity,
    from_date: fromDate,
    to_date: toDate,
    province_code: selectedProvince,
  });

  const updateProduct = (value) => {
    setProduct(value);
    setSelectedProvince(null);
  };
  const summary = data?.summary;
  const selectedName = summary?.provinces.find(
    (province) => province.code === selectedProvince,
  )?.name ?? (selectedProvince === "unassigned" ? fa.geo.unassigned : null);
  const barRows = summary
    ? [
        ...summary.provinces,
        {
          code: "unassigned",
          name: fa.geo.unassigned,
          kg: summary.unassigned_kg,
          records: summary.unassigned_records,
        },
      ]
    : [];

  return (
    <main className="trace tr-geo" dir="rtl">
      <div className="tr-top">
        <div className="tr-top-text">
          <p className="tr-eyebrow">{fa.page.eyebrow}</p>
          <h1>{fa.geo.title}</h1>
          <div className="tr-sub">{fa.geo.description}</div>
        </div>
        <div className="tr-import-filters">
          <label>
            <span>{fa.geo.product}</span>
            <select
              className="tr-select"
              value={product}
              onChange={(event) => updateProduct(event.target.value)}
            >
              <option value="beef">{fa.geo.products.beef}</option>
              <option value="lamb">{fa.geo.products.lamb}</option>
              <option value="poultry">{fa.geo.products.poultry}</option>
              <option value="oil">{fa.geo.products.oil}</option>
            </select>
          </label>
          <label>
            <span>{fa.geo.granularity}</span>
            <select
              className="tr-select"
              value={granularity}
              onChange={(event) => setGranularity(event.target.value)}
            >
              <option value="week">{fa.imports.week}</option>
              <option value="month">{fa.imports.month}</option>
              <option value="year">{fa.imports.year}</option>
            </select>
          </label>
          <label>
            <span>{fa.geo.fromDate}</span>
            <JalaliDatePicker className="tr-select" value={fromDate} onChange={setFromDate} aria-label="از تاریخ شمسی" rangeStart={fromDate} rangeEnd={toDate} />
          </label>
          <label>
            <span>{fa.geo.toDate}</span>
            <JalaliDatePicker className="tr-select" value={toDate} onChange={setToDate} aria-label="تا تاریخ شمسی" rangeStart={fromDate} rangeEnd={toDate} />
          </label>
        </div>
      </div>

      {error && <div className="tr-card tr-error">{fa.geo.error}</div>}
      {loading && !data && <div className="tr-card tr-loading">{fa.page.loading}</div>}
      {data?.summary.no_geographic_data && (
        <div className="tr-card tr-geo-no-data" role="status">
          <span aria-hidden="true">⌖</span>
          <h2>{fa.geo.noGeographicData}</h2>
          <p>{fa.geo.oilReason}</p>
        </div>
      )}
      {summary && !summary.no_geographic_data && (
        <>
          <div className="tr-geo-summary">
            <div className="tr-card tr-geo-total">
              <span>{fa.geo.totalDistribution}</span>
              <strong>{formatNumber(summary.total_kg)} {fa.geo.kgUnit}</strong>
            </div>
            <div className="tr-card tr-geo-total">
              <span>{fa.geo.unassigned}</span>
              <strong>{formatNumber(summary.unassigned_kg)} {fa.geo.kgUnit}</strong>
            </div>
            <div className="tr-card tr-geo-total">
              <span>{fa.geo.dateRange}</span>
              <strong>
                {summary.from_date ? formatJalaliDate(summary.from_date) : "—"}
                {" – "}
                {summary.to_date ? formatJalaliDate(summary.to_date) : "—"}
              </strong>
            </div>
          </div>

          <div className="tr-geo-options">
            <label>
              <input
                type="checkbox"
                checked={hideEmptyProvinces}
                onChange={(event) => setHideEmptyProvinces(event.target.checked)}
              />
              {fa.geo.hideEmpty}
            </label>
          </div>

          <div className="tr-grid">
            <section className="tr-card tr-panel">
              <div className="tr-geo-section-title">
                <h2 className="tr-title">{fa.geo.mapTitle}</h2>
                {selectedName && (
                  <button
                    type="button"
                    className="tr-geo-text-button"
                    onClick={() => setSelectedProvince(null)}
                  >
                    {fa.geo.clearProvince}: {selectedName}
                  </button>
                )}
              </div>
              <IranProvinceMap
                provinces={summary.provinces}
                totalKg={summary.total_kg}
                selectedCode={selectedProvince}
                onSelect={setSelectedProvince}
              />
            </section>
            <section className="tr-card tr-panel tr-geo-bar-card">
              <h2 className="tr-title">{fa.geo.rankedTitle}</h2>
              <GeoBarChart
                rows={barRows}
                selectedCode={selectedProvince}
                showAll={showAll}
                hideEmpty={hideEmptyProvinces}
                onToggleAll={() => setShowAll((value) => !value)}
              />
            </section>
          </div>

          <section className="tr-card tr-panel tr-geo-heatmap-card">
            <div className="tr-geo-section-title">
              <h2 className="tr-title">{fa.geo.heatmapTitle}</h2>
              <span className="tr-hint">{fa.geo.absoluteKg}</span>
            </div>
            <GeoHeatmap
              data={data.heatmap}
              hideEmpty={hideEmptyProvinces}
              onSelect={setSelectedProvince}
            />
          </section>

          <GeoReports
            summary={summary}
            cities={data.cities}
            selectedProvince={selectedProvince}
            selectedProvinceName={selectedName}
            hideEmptyProvinces={hideEmptyProvinces}
          />
        </>
      )}
    </main>
  );
}
